<template>
  <v-container
    id="restapi"
    tag="section"
  >

    <base-card
      class="pa-3"
    >
      <v-container>
        <h2 class="mt-1 mb-4 pageheader">REST API documentation</h2>
        <p>
          The Directory can be automatically queried using a public REST API.
        </p>
        <h2>URL handling</h2>
        <p>
          The basic URL to query is
          <code>
            /search/1.0/<em>format</em>
          </code>
          where &quot;format&quot; can be one of
          <code>xml</code>
          or
          <code>json</code>
          depending on the output format needed. If no format is provided,
          <code>xml</code>
          is used as the default. The response Content-Type for &quot;xml&quot; is
          <code>application/xml</code>
          and for &quot;json&quot; it is
          <code>application/json</code>.
        </p>
        <p>
          Only <code>HTTP GET</code> requests are accepted. Other HTTP verbs are rejected with HTTP status
          code 405.
          Query parameters must be appended to the URL as parameters.
        </p>
        <p>

          The XML Schema for the XML responses can be downloaded from
          <a href="files/directory-search-result-list-v1.xsd">files/directory-search-result-list-v1.xsd</a>.
        </p>
        <h2>Request parameters</h2>
        <div class="rest">
          <p>This section outlines the available request parameters and there meaning. If multiple search terms are
            used, only results matching <strong>all</strong> request parameters are returned (so it is handled like a
            boolean <em>AND</em>query). If the combination of query parameters resulted in an empty search result, an
            HTTP status code 200 with an empty result body is returned!
          </p>
          <p class="item">
          <div>
            Name:<span class="pname">q</span>
          </div>
          <div>
            Description:
            <span class="pdesc">
                        Generic query term.
                        It queries all fields and the results equal the ones that can be obtained when using the simple search.
                        The query term is split into pieces internally and subwords may be found.
                      </span>
          </div>
          <div>
            <span class="badge badge-info">Note</span>

            This parameter can occur more than once

          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/xml?q=Austrian+Government</code>
            <a :href="getTryLink('/xml?q=Austrian+Government')">Try it</a>
          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/json?q=Austrian+Government</code>
            <a :href="getTryLink('/json?q=Austrian+Government')">Try it</a>
          </div>
          </p>

          <p class="item">
          <div>
            Name:
            <span class="pname">participant</span>
          </div>
          <div>
            Description:
            <span class="pdesc">
                      Searches for exact matches in the Peppol participant identifier field
                      (the identifier scheme must be part of the value).
                    </span>
          </div>
          <div>
            <span class="badge badge-info">Note</span>

            This parameter can occur more than once but it makes no sense to do so, because
            all query parameters must match and each business card has exactly one participant identifier.

          </div>
          <div>
            <span class="badge badge-warning">Warning</span>

            If the provided parameter value cannot be converted to a Peppol participant identifier
            (as e.g. in
            <code>iso6523-actorid-upis::9915:test</code>
            ) the search will return HTTP status code 400.

          </div>
          <div>
            <span class="badge badge-warning">Warning</span>

            Pay special attention to request parameter encoding because of the special characters

            <code>#</code>
            (%23) and
            <code>:</code>
            (%3A) that can be contained in search values.

          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/xml?participant=iso6523-actorid-upis::9915:test</code>
            <a :href="getTryLink('/xml?participant=iso6523-actorid-upis::9915:test')">Try it</a>
          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/json?participant=iso6523-actorid-upis::9915:test</code>
            <a :href="getTryLink('/json?participant=iso6523-actorid-upis::9915:test')">Try it</a>
          </div>
          </p>

          <p class="item">
          <div>
            Name:
            <span class="pname">name</span>
          </div>
          <div>
            Description:

            <span class="pdesc">
                      Searches for partial matches in business entity names.
                    </span>
          </div>
          <div>
            <span class="badge badge-info">Note</span>

            This parameter can occur more than once.

          </div>
          <div>
            <span class="badge badge-warning">Warning</span>

            If the provided parameter value has ≤ 2 characters the search will return HTTP status code 400.

          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/xml?name=Government</code>
            <a :href="getTryLink('/xml?name=Government')">Try it</a>
          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/json?name=Government</code>
            <a :href="getTryLink('/json?name=Government')">Try it</a>
          </div>
          </p>

          <p class="item">
          <div>
            Name:
            <span class="pname">country</span>
          </div>
          <div>
            Description:

            <span class="pdesc">
                      Searches for exact matches in business entity country codes.
                    </span>
          </div>
          <div>
            <span class="badge badge-info">Note</span>

            This parameter can occur more than once but it makes no sense to do so, because
            all query parameters must match and each business entity has exactly one country code.

          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/xml?country=BE</code>
            <a :href="getTryLink('/xml?country=BE')">Try it</a>
          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/json?country=BE</code>
            <a :href="getTryLink('/json?country=BE')">Try it</a>
          </div>
          </p>

          <p class="item">
          <div>
            Name:
            <span class="pname">geoinfo</span>
          </div>
          <div>
            Description:

            <span class="pdesc">
                      Searches for partial matches in the geographical information.
                    </span>
          </div>
          <div>
            <span class="badge badge-info">Note</span>

            This parameter can occur more than once.

          </div>
          <div>
            <span class="badge badge-warning">Warning</span>

            If the provided parameter value has ≤ 2 characters the search will return HTTP status code 400.

          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/xml?geoinfo=Italy</code>
            <a :href="getTryLink('/xml?geoinfo=Italy')">Try it</a>
          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/json?geoinfo=Italy</code>
            <a :href="getTryLink('/json?geoinfo=Italy')">Try it</a>
          </div>
          </p>

          <p class="item">
          <div>
            Name:
            <span class="pname">identifierScheme</span>
          </div>
          <div>
            Description:

            <span class="pdesc">

                          Searches for exact matches in the additional identifier schemes.
                          Combine it with
                    <span class="pname">identifierValue</span>
                     for fine grained search results.

                </span>
          </div>
          <div>
            <span class="badge badge-info">Note</span>

            This parameter can not occur more than once.

          </div>
          <div>
            <span class="badge badge-info">Note</span>

            This field is only used for the additional identifiers and
            <strong>not</strong>

            for participant and document type identifiers!

          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/xml?identifierScheme=GLN</code>
            <a :href="getTryLink('/xml?identifierScheme=GLN')">Try it</a>
          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/json?identifierScheme=GLN</code>
            <a :href="getTryLink('/json?identifierScheme=GLN')">Try it</a>
          </div>
          </p>

          <p class="item">
          <div>
            Name:
            <span class="pname">identifierValue</span>
          </div>
          <div>
            Description:

            <span class="pdesc">

                          Searches for exact matches in the additional identifier values.
                          Combine it with
                    <span class="pname">identifierScheme</span>
                     for fine grained search results.

                </span>
          </div>
          <div>
            <span class="badge badge-info">Note</span>

            This parameter can not occur more than once.

          </div>
          <div>
            <span class="badge badge-info">Note</span>

            This field is only used for the additional identifiers and
            <strong>not</strong>

            for participant and document type identifiers!

          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/xml?identifierValue=1234567</code>
            <a :href="getTryLink('/xml?identifierValue=1234567')">Try it</a>
          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/json?identifierValue=1234567</code>
            <a :href="getTryLink('/json?identifierValue=1234567')">Try it</a>
          </div>
          </p>

          <p class="item">
          <div>
            Name:
            <span class="pname">website</span>
          </div>
          <div>
            Description:

            <span class="pdesc">
                      Searches for partial matches in the business entity websites.
                    </span>
          </div>
          <div>
            <span class="badge badge-info">Note</span>

            This parameter can occur more than once.

          </div>
          <div>
            <span class="badge badge-warning">Warning</span>

            If the provided parameter value has ≤ 2 characters the search will return HTTP status code 400.

          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/xml?website=erechnung.gv.at</code>
            <a :href="getTryLink('/xml?website=erechnung.gv.at')">Try it</a>
          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/json?website=erechnung.gv.at</code>
            <a :href="getTryLink('/json?website=erechnung.gv.at')">Try it</a>
          </div>
          </p>

          <p class="item">
          <div>
            Name:
            <span class="pname">contact</span>
          </div>
          <div>
            Description:

            <span class="pdesc">
                      Searches for partial matches in the business entity contact information.
                      It searches in all sub-fields of contact (type, name, phone number and email address).
                    </span>
          </div>
          <div>
            <span class="badge badge-info">Note</span>

            This parameter can occur more than once.

          </div>
          <div>
            <span class="badge badge-warning">Warning</span>

            If the provided parameter value has ≤ 2 characters the search will return HTTP status code 400.

          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/xml?contact=support</code>
            <a :href="getTryLink('/xml?contact=support')">Try it</a>
          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/json?contact=support</code>
            <a :href="getTryLink('/json?contact=support')">Try it</a>
          </div>
          </p>

          <p class="item">
          <div>
            Name:
            <span class="pname">addinfo</span>
          </div>
          <div>
            Description:

            <span class="pdesc">
                      Searches for partial matches in the business entity additional information.
                    </span>
          </div>
          <div>
            <span class="badge badge-info">Note</span>

            This parameter can occur more than once.

          </div>
          <div>
            <span class="badge badge-warning">Warning</span>

            If the provided parameter value has ≤ 2 characters the search will return HTTP status code 400.

          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/xml?addinfo=peppol</code>
            <a :href="getTryLink('/xml?addinfo=peppol')">Try it</a>
          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/json?addinfo=peppol</code>
            <a :href="getTryLink('/json?addinfo=peppol')">Try it</a>
          </div>
          </p>

          <p class="item">
          <div>
            Name:
            <span class="pname">regdate</span>
          </div>
          <div>
            Description:

            <span class="pdesc">

                          Searches for exact matches on a business card registration date.
                          The parameter to be searched must have the layout
                    <samp>YYYY-MM-DD</samp>

                          without a time and without a time zone.

                </span>
          </div>
          <div>
            <span class="badge badge-info">Note</span>

            This parameter can not occur more than once.

          </div>
          <div>
            <span class="badge badge-warning">Warning</span>

            If the provided parameter value can not be converted to a valid date the search will return HTTP status code
            400.

          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/xml?regdate=2012-01-01</code>
            <a :href="getTryLink('/xml?regdate=2012-01-01')">Try it</a>
          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/json?regdate=2012-01-01</code>
            <a :href="getTryLink('/json?regdate=2012-01-01')">Try it</a>
          </div>
          </p>

          <p class="item">
          <div>
            Name:
            <span class="pname">doctype</span>
          </div>
          <div>
            Description:

            <span class="pdesc">
                      Searches for exact matches in the Peppol document type identifiers field
                      (the identifier scheme must be part of the value).
                    </span>
          </div>
          <div>
            <span class="badge badge-info">Note</span>

            This parameter can not occur more than once.

          </div>
          <div>
            <span class="badge badge-info">Note</span>

            The provided parameter values is case sensitive!

          </div>
          <div>
            <span class="badge badge-warning">Warning</span>

            If the provided parameter value cannot be converted to a Peppol document type identifier
            (as e.g. in
            <code>busdox-docid-qns::urn:oasis:names:specification:ubl:schema:xsd:Invoice-2::Invoice##urn:www.cenbii.eu:transaction:biitrns010:ver2.0:extended:urn:www.peppol.eu:bis:peppol5a:ver2.0::2.1</code>
            )
            the search will return HTTP status code 400.

          </div>
          <div>
            <span class="badge badge-warning">Warning</span>

            Pay special attention to request parameter encoding because of the special characters

            <code>#</code>
            (%23) and
            <code>:</code>
            (%3A) that can be contained in search values.

          </div>
          <div>
            <span class="badge badge-primary">Example </span>
            <code>/search/1.0/xml?doctype=busdox-docid-qns%3A%3Aurn%3Aoasis%3Anames%3Aspecification%3Aubl%3Aschema%3Axsd%3AInvoice-2%3A%3AInvoice%23%23urn%3Awww.cenbii.eu%3Atransaction%3Abiitrns010%3Aver2.0%3Aextended%3Aurn%3Awww.peppol.eu%3Abis%3Apeppol5a%3Aver2.0%3A%3A2.1</code>
            <a
              :href="getTryLink('/xml?doctype=busdox-docid-qns%3A%3Aurn%3Aoasis%3Anames%3Aspecification%3Aubl%3Aschema%3Axsd%3AInvoice-2%3A%3AInvoice%23%23urn%3Awww.cenbii.eu%3Atransaction%3Abiitrns010%3Aver2.0%3Aextended%3Aurn%3Awww.peppol.eu%3Abis%3Apeppol5a%3Aver2.0%3A%3A2.1')">Try
              it</a>
          </div>
          <div class="tryme">
            <span class="badge badge-primary">Example</span>
            <code>/search/1.0/json?doctype=busdox-docid-qns%3A%3Aurn%3Aoasis%3Anames%3Aspecification%3Aubl%3Aschema%3Axsd%3AInvoice-2%3A%3AInvoice%23%23urn%3Awww.cenbii.eu%3Atransaction%3Abiitrns010%3Aver2.0%3Aextended%3Aurn%3Awww.peppol.eu%3Abis%3Apeppol5a%3Aver2.0%3A%3A2.1</code>
            <a
              :href="getTryLink('/json?doctype=busdox-docid-qns%3A%3Aurn%3Aoasis%3Anames%3Aspecification%3Aubl%3Aschema%3Axsd%3AInvoice-2%3A%3AInvoice%23%23urn%3Awww.cenbii.eu%3Atransaction%3Abiitrns010%3Aver2.0%3Aextended%3Aurn%3Awww.peppol.eu%3Abis%3Apeppol5a%3Aver2.0%3A%3A2.1')">Try
              it</a>
          </div>

          </p>
          <p class="item">
          <div>
            Name:
            <span class="pname">resultPageIndex</span>
            or
            <span class="pname">rpi</span>
          </div>
          <div>
            Description:

            <span class="pdesc">

                          Defines the 0-based index for the result page to be returned.
                          If not provided, the first page is returned.
                          By default only 20 entries are returned and this entry can be used to browse the results.
                          Use this in combination with
                    <span class="pname">resultPageCount</span>
                     to define the number of results.

                </span>
          </div>
          <div>
            <span class="badge badge-warning">Warning</span>

            Values &lt; 0 are not accepted and lead to an HTTP status code 400.

          </div>
          <div>
            <span class="badge badge-info">Note</span>
            It is not possible to return more than 1000 entries.
            Combinations of
            <span class="pname">resultPageIndex</span>
            and
            <span class="pname">resultPageCount</span>
            that would lead to such a result lead to an HTTP status code 400.

          </div>
          </p>

          <p class="item">
          <div>
            Name:
            <span class="pname">resultPageCount</span>
            or
            <span class="pname">rpc</span>
          </div>
          <div>
            Description:

            <span class="pdesc">

                          Defines the number of items to be returned per page.
                          If not provided 20 entries are returned.
                          Use this in combination with
                    <span class="pname">resultPageIndex</span>
                     to define the number of results.

                </span>
          </div>
          <div>
            <span class="badge badge-warning">Warning</span>

            Values ≤ 0 are not accepted and lead to an HTTP status code 400.

          </div>
          <div>
            <span class="badge badge-info">Note</span>

            It is not possible to return more than 1000 entries.
            Combinations of
            <span class="pname">resultPageIndex</span>
            and
            <span class="pname">resultPageCount</span>

            that would lead to such a result lead to an HTTP status code 400.

          </div>
          </p>

          <p class="item">
          <div>
            Name:<span class="pname">beautify</span>
          </div>
          <div>
            Description:

            <span class="pdesc">
                      Format the results so that they are more human readable?
                      This should only be used for debugging purposes as it increases the transferred data volume.
                      By default the returned code is minified.
                    </span>
          </div>
          <div>
            <span class="badge badge-info">Note</span>

            Any other value than
            <code>true</code>
            will disable output beautification.

          </div>
          </p>

        </div>
        <h2>Response document layout</h2>
        <p>
          This section defines the response document layout.
          Special header parameters are present in all result representations and have the following semantics:
        </p>
        <ul>
          <li>
            <code>total-result-count</code>
            the total number of matching documents
          </li>
          <li>
            <code>used-result-count</code>
            the number of results contained in the response.
            This is always ≤
            <code>total-result-count</code>
            as it is based on the paging parameters used.
          </li>
          <li>
            <code>result-page-index</code>
            is the 0-based index of the result page.

          </li>
          <li>
            <code>result-page-count</code>
            is the number of entities to show on a single page.
          </li>
          <li>
            <code>first-result-index</code>
            is the effective 0-based index of the first result item
            returned (inclusive). This is the result of
            <code>result-page-index * result-page-count</code>
            .
          </li>
          <li>
            <code>last-result-index</code>
            is the effective 0-based index of the last result item
            returned (inclusive). This is the result of
            <code>min ((result-page-index + 1) * result-page-count - 1, total-result-count - 1)</code>
            .
          </li>
          <li>
            <code>query-terms</code>
            is the combined query string that was used to perform a search.
            This is mainly for debugging purposes to cross-check which parameters took effect in searching.
          </li>
          <li>
            <code>creation-dt</code>
            the UTC date and time when this response was created.
            It is formatted according to XML Schema (XSD) rules.
          </li>
        </ul>
        <br/>
        <div class="alert alert-warning">
          The above mentioned indices relate to the found Business Entities and
          <strong>not</strong>
          to Business Cards! Internally each Business Entity is stored separately and they are combined
          on-the-fly for the result representation to avoid providing duplication data.
        </div>
        <h3>XML</h3>
        <div class="alert alert-info">
          <p>
            XML responses are always encoded in
            <samp>UTF-8</samp>.
          </p>
          <p>
            The response MIME type is always
            <code>application/xml</code>.
          </p>
          <p>Responses should not be cached.</p>
          <p>
            The XML Schema for the XML responses can be downloaded from
            <a href="files/directory-search-result-list-v1.xsd">/files/directory-search-result-list-v1.xsd</a>
            .
          </p>
        </div>
        <h4>Example response:</h4>
        <pre class="pre-scrollable">
        &lt;?xml version=&quot;1.0&quot; encoding=&quot;UTF-8&quot;?&gt;
        &lt;resultlist version=&quot;1.0&quot;
                    total-result-count=&quot;4&quot;
                    used-result-count=&quot;4&quot;
                    result-page-index=&quot;0&quot;
                    result-page-count=&quot;20&quot;
                    first-result-index=&quot;0&quot;
                    last-result-index=&quot;3&quot;
                    query-terms=&quot;q=9915:test&quot;
                    creation-dt=&quot;2017-09-20T18:13:07.468Z&quot;&gt;
          &lt;match&gt;
            &lt;participantID scheme=&quot;iso6523-actorid-upis&quot;&gt;9915:test&lt;/participantID&gt;
            &lt;docTypeID scheme=&quot;busdox-docid-qns&quot;&gt;urn:oasis:names:specification:ubl:schema:xsd:Invoice-2::Invoice##urn:www.cenbii.eu:transaction:biitrns010:ver2.0:extended:urn:www.peppol.eu:bis:peppol4a:ver2.0:extended:urn:www.erechnung.gv.at:ver1.0::2.1&lt;/docTypeID&gt;
            &lt;docTypeID scheme=&quot;busdox-docid-qns&quot;&gt;urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2::CreditNote##urn:www.cenbii.eu:transaction:biitrns014:ver2.0:extended:urn:www.peppol.eu:bis:peppol5a:ver2.0::2.1&lt;/docTypeID&gt;
            &lt;docTypeID scheme=&quot;busdox-docid-qns&quot;&gt;urn:oasis:names:specification:ubl:schema:xsd:Invoice-2::Invoice##urn:www.cenbii.eu:transaction:biitrns010:ver2.0:extended:urn:www.peppol.eu:bis:peppol5a:ver2.0::2.1&lt;/docTypeID&gt;
            &lt;docTypeID scheme=&quot;busdox-docid-qns&quot;&gt;urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2::CreditNote##urn:www.cenbii.eu:transaction:biitrns014:ver2.0:extended:urn:www.peppol.eu:bis:peppol5a:ver2.0:extended:urn:www.erechnung.gv.at:ver1.0::2.1&lt;/docTypeID&gt;
            &lt;docTypeID scheme=&quot;busdox-docid-qns&quot;&gt;urn:oasis:names:specification:ubl:schema:xsd:Invoice-2::Invoice##urn:www.cenbii.eu:transaction:biitrns010:ver2.0:extended:urn:www.peppol.eu:bis:peppol4a:ver2.0::2.1&lt;/docTypeID&gt;
            &lt;docTypeID scheme=&quot;busdox-docid-qns&quot;&gt;urn:oasis:names:specification:ubl:schema:xsd:Invoice-2::Invoice##urn:www.cenbii.eu:transaction:biitrns010:ver2.0:extended:urn:www.peppol.eu:bis:peppol5a:ver2.0:extended:urn:www.erechnung.gv.at:ver1.0::2.1&lt;/docTypeID&gt;
            &lt;entity&gt;
              &lt;name&gt;Austrian Government&lt;/name&gt;
              &lt;countryCode&gt;AT&lt;/countryCode&gt;
              &lt;website&gt;https://test.e-rechnung.gv.at&lt;/website&gt;
              &lt;additionalInfo&gt;This is the test endpoint for the whole Austrian Government&lt;/additionalInfo&gt;
              &lt;regDate&gt;2012-01-01&lt;/regDate&gt;
            &lt;/entity&gt;
          &lt;/match&gt;
          &lt;match&gt;
            &lt;participantID scheme=&quot;iso6523-actorid-upis&quot;&gt;9915:digit_test&lt;/participantID&gt;
            &lt;entity&gt;
              &lt;name&gt;DG DIGIT&lt;/name&gt;
              &lt;countryCode&gt;BE&lt;/countryCode&gt;
              &lt;geoInfo&gt;Brussels Rue Belliard 28&lt;/geoInfo&gt;
            &lt;/entity&gt;
            &lt;entity&gt;
              &lt;name&gt;Second entity name&lt;/name&gt;
              &lt;countryCode&gt;AU&lt;/countryCode&gt;
              &lt;identifier scheme=&quot;GLN&quot;&gt;123454321&lt;/identifier&gt;
              &lt;additionalInfo&gt;Whatever it takes - Peppol Directory will index this&lt;/additionalInfo&gt;
            &lt;/entity&gt;
            &lt;entity&gt;
              &lt;name&gt;Third entity - to the rescue&lt;/name&gt;
              &lt;countryCode&gt;DE&lt;/countryCode&gt;
              &lt;geoInfo&gt;Germany, Baden-Württemberg&lt;/geoInfo&gt;
              &lt;identifier scheme=&quot;PLZ&quot;&gt;79110&lt;/identifier&gt;
              &lt;additionalInfo&gt;We need more German Peppol endpoints&lt;/additionalInfo&gt;
            &lt;/entity&gt;
          &lt;/match&gt;
        &lt;/resultlist&gt;
        </pre>
        <h3>JSON</h3>
        <div class="alert alert-info">
          <p>
            JSON responses are always encoded in
            <samp>UTF-8</samp>
            .
          </p>
          <p>
            The response MIME type is always
            <code>application/json</code>
            .
          </p>
          <p>Responses should not be cached.</p>
        </div>
        <h4>Example response:</h4>
        <pre class="pre-scrollable">
        {
          &quot;version&quot;:&quot;1.0&quot;,
          &quot;total-result-count&quot;:4,
          &quot;used-result-count&quot;:4,
          &quot;result-page-index&quot;:0,
          &quot;result-page-count&quot;:20,
          &quot;first-result-index&quot;:0,
          &quot;last-result-index&quot;:3,
          &quot;query-terms&quot;:&quot;q=9915:test&quot;,
          &quot;creation-dt&quot;:&quot;2017-09-20T18:15:00.438Z&quot;,
          &quot;matches&quot;:[
            {
              &quot;participantID&quot;:{
                &quot;scheme&quot;:&quot;iso6523-actorid-upis&quot;,
                &quot;value&quot;:&quot;9915:test&quot;
              },
              &quot;docTypes&quot;:[
                {
                  &quot;scheme&quot;:&quot;busdox-docid-qns&quot;,
                  &quot;value&quot;:&quot;urn:oasis:names:specification:ubl:schema:xsd:Invoice-2::Invoice##urn:www.cenbii.eu:transaction:biitrns010:ver2.0:extended:urn:www.peppol.eu:bis:peppol4a:ver2.0:extended:urn:www.erechnung.gv.at:ver1.0::2.1&quot;
                },
                {
                  &quot;scheme&quot;:&quot;busdox-docid-qns&quot;,
                  &quot;value&quot;:&quot;urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2::CreditNote##urn:www.cenbii.eu:transaction:biitrns014:ver2.0:extended:urn:www.peppol.eu:bis:peppol5a:ver2.0::2.1&quot;
                },
                {
                  &quot;scheme&quot;:&quot;busdox-docid-qns&quot;,
                  &quot;value&quot;:&quot;urn:oasis:names:specification:ubl:schema:xsd:Invoice-2::Invoice##urn:www.cenbii.eu:transaction:biitrns010:ver2.0:extended:urn:www.peppol.eu:bis:peppol5a:ver2.0::2.1&quot;
                },
                {
                  &quot;scheme&quot;:&quot;busdox-docid-qns&quot;,
                  &quot;value&quot;:&quot;urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2::CreditNote##urn:www.cenbii.eu:transaction:biitrns014:ver2.0:extended:urn:www.peppol.eu:bis:peppol5a:ver2.0:extended:urn:www.erechnung.gv.at:ver1.0::2.1&quot;
                },
                {
                  &quot;scheme&quot;:&quot;busdox-docid-qns&quot;,
                  &quot;value&quot;:&quot;urn:oasis:names:specification:ubl:schema:xsd:Invoice-2::Invoice##urn:www.cenbii.eu:transaction:biitrns010:ver2.0:extended:urn:www.peppol.eu:bis:peppol4a:ver2.0::2.1&quot;
                },
                {
                  &quot;scheme&quot;:&quot;busdox-docid-qns&quot;,
                  &quot;value&quot;:&quot;urn:oasis:names:specification:ubl:schema:xsd:Invoice-2::Invoice##urn:www.cenbii.eu:transaction:biitrns010:ver2.0:extended:urn:www.peppol.eu:bis:peppol5a:ver2.0:extended:urn:www.erechnung.gv.at:ver1.0::2.1&quot;
                }
              ],
              &quot;entities&quot;:[
                {
                  &quot;name&quot;:&quot;Austrian Government&quot;,
                  &quot;countryCode&quot;:&quot;AT&quot;,
                  &quot;websites&quot;:[
                    &quot;https://test.e-rechnung.gv.at&quot;
                  ],
                  &quot;additionalInfo&quot;:&quot;This is the test endpoint for the whole Austrian Government&quot;,
                  &quot;regDate&quot;:&quot;2012-01-01&quot;
                }
              ]
            },
            {
              &quot;participantID&quot;:{
                &quot;scheme&quot;:&quot;iso6523-actorid-upis&quot;,
                &quot;value&quot;:&quot;9915:digit_test&quot;
              },
              &quot;entities&quot;:[
                {
                  &quot;name&quot;:&quot;DG DIGIT&quot;,
                  &quot;countryCode&quot;:&quot;BE&quot;,
                  &quot;geoInfo&quot;:&quot;Brussels Rue Belliard 28&quot;
                },
                {
                  &quot;name&quot;:&quot;Second entity name&quot;,
                  &quot;countryCode&quot;:&quot;AU&quot;,
                  &quot;identifiers&quot;:[
                    {
                      &quot;scheme&quot;:&quot;GLN&quot;,
                      &quot;value&quot;:&quot;123454321&quot;
                    }
                  ],
                  &quot;additionalInfo&quot;:&quot;Whatever it takes - Peppol Directory will index this&quot;
                },
                {
                  &quot;name&quot;:&quot;Third entity - to the rescue&quot;,
                  &quot;countryCode&quot;:&quot;DE&quot;,
                  &quot;geoInfo&quot;:&quot;Germany, Baden-Württemberg&quot;,
                  &quot;identifiers&quot;:[
                    {
                      &quot;scheme&quot;:&quot;PLZ&quot;,
                      &quot;value&quot;:&quot;79110&quot;
                    }
                  ],
                  &quot;additionalInfo&quot;:&quot;We need more German Peppol endpoints&quot;
                }
              ]
            }
          ]
        }
        </pre>
      </v-container>
    </base-card>
  </v-container>
</template>

<script>
export default {
  name: 'RestapiBody',
  data() {
    return {
      apiURL: process.env.VUE_APP_SEARCH_URL,
    }
  },
  methods: {
    getTryLink(endpoint) {
      console.log()
      return this.apiURL + endpoint
    }
  }
}
</script>

<style scoped>
.pname {
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
  font-weight: bold;
  background-color: #ddd;
  padding: 2px 4px;
  border-radius: 4px;
}

.badge-info {
  color: #fff;
  background-color: #17a2b8;
}

.badge-primary {
  color: #fff;
  background-color: #007bff;
}

.badge-warning {
  color: #212529;
  background-color: #ffc107;
}

.badge {
  display: inline-block;
  padding: 0.25em 0.4em;
  font-size: 75%;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: 0.25rem;
  transition: color .15s ease-in-out, background-color .15s ease-in-out, border-color .15s ease-in-out, box-shadow .15s ease-in-out;
}

.alert-warning {
  color: #856404;
  background-color: #fff3cd;
  border-color: #ffeeba;
}

.alert-info {
  color: #0c5460;
  background-color: #d1ecf1;
  border-color: #bee5eb;
}

.alert {
  position: relative;
  padding: 0.75rem 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;
  border-radius: 0.25rem;
}

/*code, kbd, pre, samp {*/
/*  font-size: 1em;*/
/*}*/

code, span.code {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace;
  font-size: 87.5% !important;
  color: #e83e8c !important;;
  word-wrap: break-word !important;
  font-weight: 400;
  background-color: transparent !important;
}

/*code, span.code {*/
/*  font-size: 87.5%;*/
/*  color: #e83e8c;*/
/*  word-wrap: break-word;*/
/*  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;*/
/*  padding: 0.25em 0.4em;*/
/*  font-weight: 400;*/
/*  text-align: left;*/
/*  vertical-align: baseline;*/
/*  border-radius: 0.25rem;*/
/*  transition: color .15s ease-in-out, background-color .15s ease-in-out, border-color .15s ease-in-out, box-shadow .15s ease-in-out;*/
/*}*/

.item {
  margin: 1em 0;
  border-top: solid 1px black;
}

.pre-scrollable {
  max-height: 340px;
  overflow-y: scroll;
}

a {
  color: #007bff;
  text-decoration: none;
  background-color: transparent;
}

div {
  display: block;
}
</style>
